// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how clips play (speed, loop) and the clip the phone viewer shows, so its ⋯ menu can act on it.

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {
	getStoredPlaybackRate,
	storePlaybackRate,
} from '@app/features/voice/components/media_player/utils/MediaPlaybackRates';
import {makeAutoObservable, observable} from 'mobx';

export const VIDEO_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;

type WebKitVideo = HTMLVideoElement & {
	webkitSupportsPresentationMode?: (mode: string) => boolean;
	webkitSetPresentationMode?: (mode: string) => void;
};

class VideoPlayback {
	/** The speed clips play at, shared with the desktop player (remembered the same way). */
	rate = getStoredPlaybackRate('video');
	/** Play the clip again from the start when it ends, instead of the end card. */
	loop = false;
	/** The clip open in the phone viewer. */
	activeVideo: HTMLVideoElement | null = null;

	constructor() {
		makeAutoObservable(this, {activeVideo: observable.ref}, {autoBind: true});
		void makePersistent(this, 'VideoPlayback', ['loop']);
	}

	setRate(rate: number): void {
		this.rate = Math.max(0.25, Math.min(4, rate));
		storePlaybackRate('video', this.rate);
	}

	setLoop(loop: boolean): void {
		this.loop = loop;
	}

	setActiveVideo(video: HTMLVideoElement | null): void {
		this.activeVideo = video;
	}

	get canFloat(): boolean {
		const video = this.activeVideo as WebKitVideo | null;
		if (!video) return false;
		if (typeof document !== 'undefined' && document.pictureInPictureEnabled) return true;
		return video.webkitSupportsPresentationMode?.('picture-in-picture') === true;
	}

	/** Picture in picture: the clip keeps playing in a small window over other apps. */
	float(): void {
		const video = this.activeVideo as WebKitVideo | null;
		if (!video) return;
		if (typeof video.requestPictureInPicture === 'function' && document.pictureInPictureEnabled) {
			void video.requestPictureInPicture().catch(() => {});
			return;
		}
		video.webkitSetPresentationMode?.('picture-in-picture');
	}
}

export default new VideoPlayback();

export function formatSpeed(rate: number): string {
	return `${rate}×`;
}
